import {
  Box,
  Chip,
  Divider,
  ListItem,
  ListItemText,
  Typography,
} from '@material-ui/core';
import { Link } from '@backstage/core-components';
import { HighlightedSearchResultText } from '@backstage/plugin-search-react';
import { ResultHighlight } from '@backstage/plugin-search-common';
import { EntityRefLink } from '@backstage/plugin-catalog-react';
import { relativeTime } from '../releases/relativeTime';

/** The extra fields the releases collator puts on its documents. */
type ReleaseSearchDocument = {
  title: string;
  text: string;
  location: string;
  entityRef?: string;
  tagName?: string;
  releasedAt?: string;
};

/**
 * Search result for a release changelog (type 'releases', indexed by
 * packages/backend/src/modules/releasesSearchCollator.ts). Shows the
 * component it belongs to and when it shipped, since those are what
 * distinguish two otherwise similar changelog matches.
 */
export const ReleaseSearchResultListItem = (props: {
  result?: ReleaseSearchDocument;
  highlight?: ResultHighlight;
  rank?: number;
}) => {
  const { result, highlight } = props;
  if (!result) {
    return null;
  }

  return (
    <>
      <ListItem alignItems="flex-start">
        <ListItemText
          primaryTypographyProps={{ variant: 'h6' }}
          primary={
            <Link to={result.location}>
              {highlight?.fields.title ? (
                <HighlightedSearchResultText
                  text={highlight.fields.title}
                  preTag={highlight.preTag}
                  postTag={highlight.postTag}
                />
              ) : (
                result.title
              )}
            </Link>
          }
          secondary={
            <Typography
              component="span"
              variant="body2"
              color="textSecondary"
              noWrap
              display="block"
            >
              {highlight?.fields.text ? (
                <HighlightedSearchResultText
                  text={highlight.fields.text}
                  preTag={highlight.preTag}
                  postTag={highlight.postTag}
                />
              ) : (
                result.text
              )}
            </Typography>
          }
        />
        <Box display="flex" alignItems="center" flexShrink={0} ml={2}>
          <Chip label="Release" size="small" />
          {result.releasedAt && (
            <Typography variant="caption" color="textSecondary">
              {relativeTime(result.releasedAt)}
            </Typography>
          )}
        </Box>
      </ListItem>
      {result.entityRef && (
        <Box pl={2} pb={1}>
          <Typography variant="caption" color="textSecondary">
            in <EntityRefLink entityRef={result.entityRef} />
          </Typography>
        </Box>
      )}
      <Divider component="li" />
    </>
  );
};
